import { Control, Controller, useFormState } from "react-hook-form";
import {
  FormattedMessage,
  defineMessages,
  useIntl,
  type IntlShape,
  type MessageDescriptor,
} from "react-intl";

import {
  Button,
  Combobox,
  Field,
  makeStyles,
  mergeClasses,
  Option,
  Radio,
  RadioGroup,
  tokens,
} from "@fluentui/react-components";

import { Brand, CONNECTED, getBrandFromEmail } from "@aglocal/brands";
import type { Interview } from "@aglocal/schema/Interview";
import { mediaQuery } from "@aglocal/web/helpers/responsive";
import useMatchMedia from "@aglocal/web/hooks/useMatchMedia";

import Container from "@aglocal/web/components/Container";

import Copyright from "@/components/Copyright";

import { PostMeetingFormWithSummary } from "./form";

import { ActivityType } from "@aglocal/types";

interface MeetingTypeOption {
  value: ActivityType;
  label: string;
}

const MEETING_TYPE_LABELS = defineMessages({
  G2: {
    defaultMessage: "G2",
    description:
      "Meeting type option on the wrap-up form; G2 is an Allegis term for a get-to-know-you meeting.",
  },
  STP: {
    defaultMessage: "Service Touchpoint",
    description: "Meeting type option on the wrap-up form.",
  },
  "Video Interview": {
    defaultMessage: "Video Interview",
    description: "Meeting type option on the wrap-up form.",
  },
  IOI: {
    defaultMessage: "Internal Interview",
    description: "Meeting type option on the wrap-up form.",
  },
  Other: {
    defaultMessage: "Other",
    description: "Meeting type option on the wrap-up form.",
  },
}) satisfies Record<ActivityType, MessageDescriptor>;

const messages = defineMessages({
  meetingType: {
    defaultMessage: "Meeting Type",
    description: "Label of the meeting type selector on the wrap-up form.",
  },
  save: {
    defaultMessage: "Update Connected",
    description: "Button that saves the wrap-up form to Connected.",
  },
  saved: {
    defaultMessage: "Connected Updated",
    description:
      "Label of the save button after the wrap-up form was saved to Connected.",
  },
  hideDetails: {
    defaultMessage: "Hide Meeting Details",
    description: "Button that closes the side drawer on the wrap-up page.",
  },
  showDetails: {
    defaultMessage: "Show Meeting Details",
    description: "Button that opens the side drawer on the wrap-up page.",
  },
});

function getMeetingTypeOptions(
  intl: IntlShape,
  brand: Brand | null,
): MeetingTypeOption[] {
  return (brand ?? CONNECTED).allowedActivityTypes.map((value) => ({
    value,
    label: intl.formatMessage(MEETING_TYPE_LABELS[value]),
  }));
}

const useStyles = makeStyles({
  root: {
    backgroundColor: tokens.colorNeutralBackground1,
    borderTop: `${tokens.strokeWidthThin} solid ${tokens.colorNeutralStroke1}`,
  },
  container: {
    paddingTop: tokens.spacingVerticalM,
    paddingBottom: tokens.spacingVerticalM,
    display: "flex",
    flexDirection: "column",
    alignItems: "stretch",
    columnGap: tokens.spacingHorizontalM,
    rowGap: tokens.spacingVerticalM,
    [mediaQuery({ min: "xl" })]: {
      flexDirection: "row",
      alignItems: "center",
      justifyContent: "space-between",
    },
  },
  form: {
    display: "flex",
    flexDirection: "column",
    columnGap: tokens.spacingHorizontalM,
    rowGap: tokens.spacingVerticalM,
    [mediaQuery({ min: "xl" })]: {
      display: "flex",
      flexDirection: "row",
      alignItems: "center",
    },
  },
  meetingTypeField: {
    whiteSpace: "nowrap",
    [mediaQuery({ min: "md" })]: {
      display: "flex",
      flexDirection: "row",
      alignItems: "center",
      columnGap: tokens.spacingHorizontalS,
    },
  },
  meetingTypeInput: {
    "& > *": {
      whiteSpace: "nowrap",
    },
    [mediaQuery({ min: "md" })]: {
      display: "flex",
      flexDirection: "row",
      alignItems: "center",
    },
  },
  button: {
    whiteSpace: "nowrap",
  },
});

export interface PostMeetingFooterProps {
  className?: string;
  control: Control<PostMeetingFormWithSummary>;
  drawerIsOpen: boolean;
  setDrawerIsOpen: (isOpen: boolean) => void;
  interview: Interview;
  submitSuccess: boolean;
  isMeetingTypeDisabled?: boolean;
  disableSave: boolean;
}

export default function PostMeetingFooter({
  className,
  control,
  drawerIsOpen,
  setDrawerIsOpen,
  interview,
  submitSuccess,
  isMeetingTypeDisabled = false,
  disableSave = false,
}: PostMeetingFooterProps) {
  const intl = useIntl();
  const styles = useStyles();
  const isSM = useMatchMedia({ max: "sm" });

  const { isSubmitting } = useFormState({
    control,
    name: [],
  });

  // Get only allowed options
  const brand = getBrandFromEmail(interview.interviewer?.uId ?? "");
  const meetingTypeOptions = getMeetingTypeOptions(intl, brand);

  return (
    <footer className={mergeClasses(styles.root, className)}>
      <Container className={styles.container}>
        <div className={styles.form}>
          <Controller
            control={control}
            name="meetingType"
            render={({
              field: { onChange, ...field },
              fieldState: { error },
              formState: { isSubmitting },
            }) => (
              <Field
                label={intl.$t(messages.meetingType)}
                className={styles.meetingTypeField}
                orientation="vertical"
                validationState={error ? "error" : undefined}
                validationMessage={error?.message}
              >
                {isSM ? (
                  <Combobox
                    {...field}
                    disabled={
                      isSubmitting ||
                      submitSuccess ||
                      isMeetingTypeDisabled ||
                      disableSave
                    }
                    onOptionSelect={(event, { optionValue }) => {
                      if (!isMeetingTypeDisabled) {
                        onChange(optionValue);
                      }
                    }}
                  >
                    {meetingTypeOptions.map(({ value, label }) => (
                      <Option key={value} value={value}>
                        {label}
                      </Option>
                    ))}
                  </Combobox>
                ) : (
                  <RadioGroup
                    {...field}
                    onChange={(event, { value }) => {
                      if (!isMeetingTypeDisabled) {
                        onChange(value);
                      }
                    }}
                    layout="vertical"
                    className={styles.meetingTypeInput}
                    disabled={
                      isSubmitting ||
                      submitSuccess ||
                      isMeetingTypeDisabled ||
                      disableSave
                    }
                  >
                    {meetingTypeOptions.map(({ value, label }) => (
                      <Radio key={value} value={value} label={label} />
                    ))}
                  </RadioGroup>
                )}
              </Field>
            )}
          />
          <Button
            type="submit"
            className={styles.button}
            appearance="primary"
            disabled={isSubmitting || submitSuccess || disableSave}
          >
            <FormattedMessage
              {...(submitSuccess ? messages.saved : messages.save)}
            />
          </Button>
          <Button
            className={styles.button}
            appearance="secondary"
            onClick={() => {
              setDrawerIsOpen(!drawerIsOpen);
            }}
          >
            <FormattedMessage
              {...(drawerIsOpen ? messages.hideDetails : messages.showDetails)}
            />
          </Button>
        </div>
        <div style={{ textAlign: "center" }}>
          <Copyright />
        </div>
      </Container>
    </footer>
  );
}
